<script>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ArrowLeft } from '@element-plus/icons-vue'
import MarkdownBody from '@/components/MarkdownBody.vue'
import MonoJsonEdit from '@/components/MonoJsonEdit.vue'

export default {
  name: 'AdminExamDetailView',
  components: { MarkdownBody, MonoJsonEdit },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const exam = ref(null)
    const loading = ref(false)
    const examId = ref(route.params.id)

    const getAuthHeaders = () => ({
      Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
      'Content-Type': 'application/json'
    })

    const fetchExam = async () => {
      try {
        loading.value = true
        const response = await axios.get(`/api/v1/exam/${examId.value}`, {
          headers: getAuthHeaders()
        })
        if (response.data.code === 200) {
          exam.value = response.data.data
        } else {
          ElMessage.error(response.data.message || '获取测评详情失败')
        }
      } catch (error) {
        console.error('Error fetching exam:', error)
        ElMessage.error('获取测评详情失败')
      } finally {
        loading.value = false
      }
    }

    const formatDate = (timestamp) => {
      if (!timestamp) return ''
      return new Date(timestamp * 1000).toLocaleString('zh-CN')
    }

    const editExam = () => router.push(`/admin/exam/edit/${examId.value}`)
    const exportYaml = () => router.push(`/admin/exam/${examId.value}/yaml`)

    const deleteExam = async () => {
      try {
        await ElMessageBox.confirm('确定要删除这个测评吗？此操作不可恢复。', '警告', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        const response = await axios.post(
          '/api/v1/exam/delete',
          { id: parseInt(examId.value, 10) },
          { headers: getAuthHeaders() }
        )
        if (response.data.code === 200) {
          ElMessage.success('删除成功')
          router.push('/admin/exames')
        } else {
          ElMessage.error(response.data.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }

    const goBack = () => router.push('/admin/exames')

    onMounted(fetchExam)

    return {
      exam,
      loading,
      formatDate,
      editExam,
      exportYaml,
      deleteExam,
      goBack,
      ArrowLeft
    }
  }
}
</script>

<template>
  <div class="admin-page admin-exam-detail" v-loading="loading">
    <div class="page-toolbar">
      <el-button :icon="ArrowLeft" @click="goBack">返回测评列表</el-button>
      <div v-if="exam" class="toolbar-actions">
        <el-button type="warning" @click="editExam">编辑</el-button>
        <el-button type="info" @click="exportYaml">导出 YAML</el-button>
        <el-button type="danger" @click="deleteExam">删除</el-button>
      </div>
    </div>

    <template v-if="exam">
      <div class="exam-header">
        <h1>{{ exam.examName }}</h1>
        <div class="exam-meta">
          <span v-if="exam.examSname">英文简称：{{ exam.examSname }}</span>
          <span>创建时间：{{ formatDate(exam.createdAt) }}</span>
          <span>题目数量：{{ exam.questionCount }}</span>
          <span v-if="exam.timeLimit">限时：{{ exam.timeLimit }} 分钟</span>
          <span>价格：
            <template v-if="exam.free === 1">免费（永久）</template>
            <template v-else-if="exam.fee === 0 && exam.originFee > 0">
              <span class="price-original">原价 ¥{{ (exam.originFee / 100).toFixed(2) }}</span>
              <el-tag type="danger" effect="light" size="small">限时免费</el-tag>
            </template>
            <template v-else-if="exam.fee > 0 && exam.originFee > exam.fee">
              成交 ¥{{ (exam.fee / 100).toFixed(2) }}
              <span class="price-original">原价 ¥{{ (exam.originFee / 100).toFixed(2) }}</span>
              <el-tag type="warning" effect="light" size="small">限时优惠</el-tag>
            </template>
            <template v-else-if="exam.fee === 0">免费</template>
            <template v-else>成交 ¥{{ (exam.fee / 100).toFixed(2) }}</template>
          </span>
          <span>点赞：{{ exam.praise }}</span>
        </div>
      </div>

      <div class="exam-content">
        <div v-if="exam.cover" class="cover-banner">
          <img :src="exam.cover" alt="封面" class="cover" />
        </div>

        <div class="exam-info">
          <h2>测评简介</h2>
          <p class="profile">{{ exam.profile }}</p>

          <h2>详细说明</h2>
          <MarkdownBody :content="exam.description" empty-text="暂无详细说明" />

          <template v-if="exam.feedbacks">
            <h2>测评反馈</h2>
            <MonoJsonEdit :model-value="exam.feedbacks" read-only height="min(24vh, 280px)" />
          </template>

          <template v-if="exam.summarys">
            <h2>测评总结</h2>
            <MonoJsonEdit :model-value="exam.summarys" read-only height="min(24vh, 280px)" />
          </template>

          <template v-if="exam.stops">
            <h2>试题中断提示</h2>
            <MonoJsonEdit :model-value="exam.stops" read-only height="min(20vh, 240px)" />
          </template>

          <div class="info-list">
            <h2>其他信息</h2>
            <ul>
              <li>
                <span class="label">分类ID：</span>
                <span class="value">{{ exam.categoryId }}</span>
              </li>
              <li>
                <span class="label">审核状态：</span>
                <span
                  class="value"
                  :class="{
                    'status-pending': exam.auditStatus === 0,
                    'status-approved': exam.auditStatus === 1,
                    'status-rejected': exam.auditStatus === 2
                  }"
                >
                  {{ exam.auditStatus === 0 ? '未审核' : exam.auditStatus === 1 ? '审核通过' : '审核驳回' }}
                </span>
              </li>
              <li v-if="exam.auditMessage">
                <span class="label">审核信息：</span>
                <span class="value">{{ exam.auditMessage }}</span>
              </li>
              <li v-if="exam.auditTime">
                <span class="label">审核时间：</span>
                <span class="value">{{ formatDate(exam.auditTime) }}</span>
              </li>
              <li>
                <span class="label">状态：</span>
                <span
                  class="value"
                  :class="{
                    'status-normal': exam.state === 0,
                    'status-deleted': exam.state === 1
                  }"
                >
                  {{ exam.state === 0 ? '正常' : '已删除' }}
                </span>
              </li>
              <li>
                <span class="label">是否下架：</span>
                <span class="value">{{ exam.removed ? '是' : '否' }}</span>
              </li>
              <li>
                <span class="label">是否热门：</span>
                <span class="value">{{ exam.isHot ? '是' : '否' }}</span>
              </li>
              <li>
                <span class="label">首页展示：</span>
                <span class="value">{{ exam.homeDisplay ? '是' : '否' }}</span>
              </li>
            </ul>
          </div>

          <div class="question-info">
            <h2>题目信息</h2>
            <div class="question-keys" v-if="exam.questionKeys">
              <h3>题目 Key 集合</h3>
              <MonoJsonEdit :model-value="exam.questionKeys" read-only height="min(24vh, 280px)" />
            </div>
            <div class="original-questions" v-if="exam.originalQuestions">
              <h3>原始题目（含分值与统计维度）</h3>
              <MonoJsonEdit :model-value="exam.originalQuestions" read-only height="min(48vh, 520px)" />
            </div>
            <div class="questions" v-if="exam.questions">
              <h3>答题卷（系统自动生成）</h3>
              <p class="field-hint">由保存时的原始题目脱敏生成，供 C 端答题使用。</p>
              <MonoJsonEdit :model-value="exam.questions" read-only height="min(48vh, 520px)" />
            </div>
            <div v-if="exam.maxScore" class="max-score-block">
              <h3>统计维度满分 (max_score)</h3>
              <MonoJsonEdit :model-value="exam.maxScore" read-only height="min(20vh, 240px)" />
            </div>
          </div>
        </div>
      </div>
    </template>

    <el-empty v-else-if="!loading" description="未找到测评信息" />
  </div>
</template>

<style scoped>
.admin-exam-detail {
  padding: 0 4px;
}

.page-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.toolbar-actions {
  display: flex;
  gap: 10px;
}

.exam-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--el-border-color-light);
}

.exam-header h1 {
  margin: 0 0 10px;
  font-size: 24px;
}

.exam-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  color: var(--el-text-color-secondary);
  font-size: 14px;
}

.price-original {
  text-decoration: line-through;
  color: #909399;
  margin-right: 6px;
}

.exam-content {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.cover-banner {
  width: 100%;
  background: var(--el-fill-color-light);
}

.cover {
  width: 100%;
  max-height: 320px;
  object-fit: cover;
  display: block;
}

.exam-info {
  padding: 24px 20px 20px;
}

.exam-info h2 {
  margin: 24px 0 12px;
  font-size: 18px;
}

.exam-info h2:first-child {
  margin-top: 0;
}

.profile,
.feedbacks,
.summarys {
  margin: 0;
  line-height: 1.7;
  color: var(--el-text-color-regular);
  white-space: pre-wrap;
}

.info-list ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.info-list li {
  display: flex;
  margin-bottom: 8px;
  font-size: 14px;
}

.info-list .label {
  min-width: 88px;
  color: var(--el-text-color-secondary);
}

.status-pending {
  color: var(--el-color-warning);
}
.status-approved,
.status-normal {
  color: var(--el-color-success);
}
.status-rejected,
.status-deleted {
  color: var(--el-color-danger);
}

.question-info {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--el-border-color-light);
}

.question-info h3 {
  margin: 16px 0 8px;
  font-size: 15px;
}

.question-info p {
  margin: 0;
  padding: 12px;
  background: var(--el-fill-color-light);
  border-radius: 4px;
  white-space: pre-wrap;
}

.content-viewer,
.json-viewer {
  background: var(--el-fill-color-light);
  padding: 12px;
  border-radius: 4px;
  white-space: pre-wrap;
  font-family: monospace;
  font-size: 13px;
}
</style>
